import './Auth.sass';

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authActions, authSelector } from '$store/auth';
import { Button, Form, FormItem, Input, Placeholder } from '$uikit';
import { useGoogleAuth, useTranslator } from '$hooks';
import { IcGoogle24, IcKey28, IcMail28, IcTelegram24 } from '$assets';
import {
  DisplayMode,
  getDisplayMode,
  getDocumentHref,
  getForceGmailOAuth,
  getStorageItem,
  getTargetModal,
  getUtmLocal,
  isIsolated,
  setStorageItem,
  statReachGoal,
} from '$utils';
import { AuthProps } from './Auth.interface';
import { AuthPopoverState } from '$store/models';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import WebApp from '@twa-dev/sdk';
import { toastActions } from '$store/toast';
import { authorizationCode, authorizationDefault } from '$passkey/authorization';
import { authenticate } from '$passkey/ceremony';
import { PasskeyRedirect, apiMessage } from '$passkey/http';
import { profileActions, profileSelector } from '$store/profile';
import { setSubscriptionContentPriority } from '$utils/subscriptionAuthor';
import { ContentPriorityType } from '$store/main/interface';
import {
  init as initTelegramLogin,
  getMethod,
  openOidc,
  openWidget,
  isOidcError,
  AuthMethod,
} from 'services/telegramLogin';

const TELEGRAM_LINK_URL_MAX_LENGTH = 255;

function buildRedirectUrl(): string {
  let resultUrl = location.pathname + location.search;
  try {
    const url = new URL(location.href);
    const utm = getUtmLocal();

    if (utm?.source && !url.searchParams.get('utm_source')) {
      url.searchParams.set('utm_source', utm.source || '');
      url.searchParams.set('utm_campaign', utm.campaign || '');
      url.searchParams.set('visitor_id', utm.visitorId || '');
      url.searchParams.set('extra_param1', utm.extraParam1 || '');
      url.searchParams.set('extra_param2', utm.extraParam2 || '');
      url.searchParams.set('extra_param3', utm.extraParam3 || '');
    }

    const ref: any = getStorageItem('ref');
    if (ref && !url.searchParams.get('ref')) {
      url.searchParams.set('ref', ref);
    }

    const targetModal = getTargetModal();
    if (targetModal) {
      url.searchParams.set('w', targetModal as string);
    }

    resultUrl = url.pathname + url.search;
  } catch {}
  return resultUrl;
}

export const Auth = ({ onEmailSendClick }: AuthProps) => {
  const dispatch = useDispatch();
  const t = useTranslator();
  const signIn = useGoogleAuth();
  const { authPopoverState } = useSelector(profileSelector);
  const { telegramLinkData } = useSelector(authSelector);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isEmailSent, setEmailSent] = useState(false);

  useEffect(() => {
    statReachGoal('show_auth_form');
    setSubscriptionContentPriority(ContentPriorityType.ADULT);
    initTelegramLogin();
  }, []);

  useEffect(() => {
    onEmailSendClick && onEmailSendClick(isEmailSent);
  }, [isEmailSent]);

  const startLinkAuth = useCallback(() => {
    const url = buildRedirectUrl();
    dispatch(
      authActions.telegramRequestLink({
        // Backend rejects longer urls; this tab returns to its target on its own
        url: url.length > TELEGRAM_LINK_URL_MAX_LENGTH ? '' : url,
        pwa: getDisplayMode() === DisplayMode.STANDALONE ? 1 : 0,
        isolated: isIsolated() ? 1 : 0,
      }),
    );
  }, []);

  const startWidgetAuth = useCallback(() => {
    openWidget()
      .then((result) => dispatch(authActions.telegramAuth(result)))
      .catch(() => startLinkAuth());
  }, [startLinkAuth]);

  const startOidcAuth = useCallback(() => {
    openOidc()
      .then((data) => {
        if (isOidcError(data)) {
          if (data.error !== 'popup_closed') startWidgetAuth();
          return;
        }
        dispatch(authActions.telegramOidcAuth({ id_token: data.id_token }));
      })
      .catch(() => startWidgetAuth());
  }, [startWidgetAuth]);

  const handleTelegram = useCallback(async () => {
    statReachGoal('auth_form_telegram');
    statReachGoal('auth_form_btn_click');

    const method: AuthMethod = await getMethod();

    switch (method) {
      case 'oidc':
        return startOidcAuth();
      case 'widget':
        return startWidgetAuth();
      case 'link':
        return startLinkAuth();
    }
  }, [startOidcAuth, startWidgetAuth, startLinkAuth]);

  const handlePasskey = useCallback(() => {
    statReachGoal('auth_form_passkey');
    statReachGoal('auth_form_btn_click');
    if (window.isWebApp || !browserSupportsWebAuthn()) {
      if (window.isWebApp) {
        WebApp.openLink('https://ton.place');
      }
      dispatch(toastActions.setToastFail(t('passkey_not_supported')));
      return;
    }
    authenticate().then(
      (session) => dispatch(authActions.passkeyAuth(session)),
      (error) => {
        if (error instanceof PasskeyRedirect) return;
        const code = authorizationCode(error);
        dispatch(
          toastActions.setToastFail(
            code
              ? t(code, { defaultValue: authorizationDefault(code) })
              : apiMessage(error) ??
                  t('passkey_auth_error', {
                    defaultValue: authorizationDefault('passkey_auth_error'),
                  }),
          ),
        );
      },
    );
  }, [dispatch, t]);

  const handleGoogle = useCallback(() => {
    setStorageItem('google_target_url', location.pathname + location.search);
    statReachGoal('auth_form_google');
    statReachGoal('auth_form_btn_click');
    signIn();
  }, [signIn]);

  const handleEmail = useCallback(() => {
    if (email?.match(/@gmail\.com$/) && getForceGmailOAuth()) {
      return handleGoogle();
    }

    dispatch(
      authActions.emailAuth({
        email: email.trim(),
        onDone: () => setEmailSent(true),
      }),
    );

    statReachGoal('auth_form_email');
    statReachGoal('auth_form_btn_click');
  }, [email, handleGoogle]);

  const handleEmailChange = useCallback(
    ({ target }) => {
      if (!email) statReachGoal('auth_form_email_fill');
      setEmail(target.value);
    },
    [email],
  );

  const handleChangeCode = useCallback(
    ({ target }) => {
      if (!code) statReachGoal('auth_form_code_fill');
      setCode((target.value || '').replace(/[^0-9]/g, ''));
    },
    [code],
  );

  const handleBack = useCallback(() => {
    setEmailSent(false);
    setEmail('');
  }, []);

  const handleValidateCode = useCallback(() => {
    dispatch(authActions.validateEmailCode({ email, code }));
    statReachGoal('auth_form_email_send_code');
  }, [email, code]);

  function renderContent() {
    if (telegramLinkData) {
      return (
        <Placeholder
          icon={<IcTelegram24 style={{ width: 72, height: 72 }} />}
          iconSize={72}
          actions={
            <>
              <Button href={telegramLinkData.linkTg} target="_blank" stretched>
                {t('auth_telegram_link_btn')}
              </Button>
              <Button
                href={telegramLinkData.link}
                target="_blank"
                stretched
                mode="inline"
              >
                {t('auth_telegram_link_no_btn')}
              </Button>
              <Button
                mode="quinary"
                stretched
                onClick={() => dispatch(authActions.clearTelegramLinkData())}
              >
                {t('cancel')}
              </Button>
            </>
          }
        >
          {t('auth_telegram_link_caption')}
        </Placeholder>
      );
    }

    if (isEmailSent) {
      return (
        <>
          <IcMail28 className="Auth__email__icon" />
          <div className="Auth__email__title">{t('auth_email_title')}</div>
          <div className="Auth__email__description">
            {t('auth_email_description_head')} <span>{email.trim()}</span>
            {t('auth_email_description_tail')}
          </div>
          <Form className="Auth__email__form">
            <FormItem>
              <Input
                placeholder={t('auth_email_sent_input_placeholder')}
                value={code}
                onChange={handleChangeCode}
              />
            </FormItem>
            <div className="Auth__email__controls">
              <FormItem>
                <Button onClick={handleValidateCode}>{t('continue')}</Button>
              </FormItem>
              <FormItem>
                <Button mode="quinary" onClick={handleBack}>
                  {t('back')}
                </Button>
              </FormItem>
            </div>
          </Form>
        </>
      );
    }

    return (
      <>
        <div className="Auth__socials">
          <Button
            stretched
            onClick={handleTelegram}
            mode="light"
            icon={<IcTelegram24 />}
            className="Auth__telegramBtn"
          >
            {t('main_login_telegram')}
          </Button>
          <Button
            stretched
            mode="light"
            onClick={handleGoogle}
            style={{ marginTop: 10 }}
            icon={<IcGoogle24 />}
          >
            {t('main_login_google')}
          </Button>
          {authPopoverState === AuthPopoverState.LOGIN && browserSupportsWebAuthn() && (
            <Button
              stretched
              mode="light"
              onClick={handlePasskey}
              style={{ marginTop: 10 }}
              icon={<IcKey28 style={{ width: 24, height: 24 }} />}
            >
              {t('main_login_passkey')}
            </Button>
          )}
        </div>
        <div className="Auth__divider">
          <span>{t('auth_or')}</span>
        </div>
        <Form>
          <FormItem title="Email" className="Auth__email__input">
            <Input
              placeholder={t('auth_enter_email')}
              value={email}
              onChange={handleEmailChange}
            />
          </FormItem>
          <FormItem>
            <Button onClick={handleEmail}>{t('auth_email_button')}</Button>
          </FormItem>
        </Form>
        <div
          className="Auth__changeMode__button"
          onClick={() =>
            dispatch(
              profileActions.setAuthPopoverState(
                authPopoverState === AuthPopoverState.REGISTER
                  ? AuthPopoverState.LOGIN
                  : AuthPopoverState.REGISTER,
              ),
            )
          }
        >
          {authPopoverState === AuthPopoverState.LOGIN ? (
            <>
              {t('auth_create_inline_caption')}{' '}
              <span>{t('auth_sign_up_inline_button')}</span>
            </>
          ) : (
            <>
              {t('auth_login_inline_caption')}{' '}
              <span>{t('auth_login_inline_button')}</span>
            </>
          )}
        </div>
        <div className="Auth__privacy">
          {t('auth_terms_pref')}{' '}
          <a href={getDocumentHref('terms')} target="_blank" rel="noreferrer">
            {t('auth_terms')}
          </a>
          {` ${t('and')} `}
          <a href="/privacy.html" target="_blank">
            {t('auth_privacy')}
          </a>
          {t('auth_age')}
        </div>
      </>
    );
  }

  return <div className="Auth">{renderContent()}</div>;
};

Auth.displayName = 'Auth';
